import {computed, ref} from "vue";
import {useGrid} from "./useGrid.js";
import seedrandom from 'seedrandom';
import {useSound} from "./useSound.js";
import {useLiveAdapter} from "./liveAdapter.js";
import {useAchievements} from "./useAchievements.js";
import {STARTING_HEARTS, MAX_HEARTS, WRONG_WORD_PENALTY, SERIES_LENGTH, MOMENTUM_LETTER, MOMENTUM_WORD, momentumMultiplier} from "./gameConstants.js";


// The adapter abstracts where the puzzle comes from and what a guess/complete/game-over
// persists to. Defaults to the live daily game (server-backed UserState); the archive
// practice mode passes a local adapter that touches no leaderboard state.
export const useGameEngine = (adapter = useLiveAdapter()) => {
    const {currentWords, loading: adapterLoading} = adapter;
    const {grid, selectedLetter, populateGrid, revealWords, foundWords, revealedCountForWord, eligibleWords} = useGrid();
    const {playCorrect, playWrong, playWordCorrect, playWordWrong, playFinish, playGameOver} = useSound();
    // Achievements are awarded server-side; the engine just relays the ids the server reports
    // as newly unlocked into the toast queue. Archive practice returns none, so this no-ops there.
    const {unlock: unlockAchievements} = useAchievements();
    const STARTED = "started";
    const COMPLETED = "completed";
    const FAILED = "failed";

    let score = ref(0);
    let xp = ref(adapter.initialState().xp ?? 0);

    let hearts = ref(STARTING_HEARTS);
    // Momentum meter: correct deductions in a row this run (+1 letter, +5 word), reset by any wrong
    // guess. Unlike most per-round state it carries ACROSS puzzles in a run, so it's only seeded (on
    // load) and never reset by nextGame. Drives the XP multiplier the meter displays.
    let momentum = ref(0);
    // Wrong guesses on the current puzzle (used to award precision achievements like Flawless).
    // Hints don't count. Reset per puzzle; seeded from resumed state on (re)load.
    let mistakesThisPuzzle = ref(0);
    // Bumped on every actual wrong guess (letter or word) so the UI can fire a "you lost a
    // heart" reaction (board shake). Deliberately not touched by hints or resume seeding, which
    // also drain/restore hearts but aren't mistakes the player should be punished for visually.
    let wrongGuessNonce = ref(0);
    // Run-level tallies for the end-of-run report card. Session-scoped: reset when the engine
    // (re)loads in setUserState and accumulated across the run's puzzles. A mid-run reload restarts
    // them, so the card reflects this sitting rather than being authoritative all-time stats (those
    // live in the Stats modal). xpStart baselines the run's XP so we can show the delta earned.
    let runXpStart = ref(0);
    let runBestMomentum = ref(0);
    let runFlawless = ref(0);
    let runCorrectGuesses = ref(0);
    let runTotalGuesses = ref(0);

    // Per-round stats for the in-the-moment skill badges, sent on completion. A puzzle resumed
    // mid-way (reload) can't be measured fairly, so roundResumed disqualifies it from these.
    let roundStartMs = 0;
    let letterGuessCount = 0;
    let maxHiddenAtCommit = 0;
    let lowestHearts = STARTING_HEARTS;
    // Hints spent this puzzle (for the hint badges) and whether one was taken at the lowest hearts a
    // hint is allowed (2), which a clear turns into a clutch save.
    let hintsUsed = 0;
    let clutchHint = false;
    let roundResumed = false;

    function beginRound(resumed = false) {
        roundStartMs = Date.now();
        letterGuessCount = 0;
        maxHiddenAtCommit = 0;
        lowestHearts = hearts.value;
        hintsUsed = 0;
        clutchHint = false;
        roundResumed = resumed;
    }

    function trackLowHearts() {
        lowestHearts = Math.min(lowestHearts, hearts.value);
    }
    let seenLetters = ref([]);
    let words = ref([]);
    // gates the game-over screen (which mounts the leaderboard) until the final
    // score has actually been submitted, so the "You're Xth today!" banner shows
    let resultsReady = ref(false);

    let state = ref(STARTED);

    async function initialiseGame() {
        // Live adapter re-pulls server state here so a remount (e.g. returning from an
        // archive run) reflects real progress rather than a stale load-time snapshot.
        // Archive has no refresh: its state is local and always current.
        if (adapter.refresh) {
            await adapter.refresh();
        }
        await setUserState();
        await adapter.loadWords();
        await setWordState();
    }

    async function setUserState() {
        const initial = adapter.initialState();
        score.value = initial.score ?? 0;
        hearts.value = initial.hearts ?? STARTING_HEARTS;
        momentum.value = initial.momentum ?? 0;
        // Baseline the run report against the state we're loading from.
        runXpStart.value = initial.xp ?? 0;
        runBestMomentum.value = initial.momentum ?? 0;
        runFlawless.value = 0;
        runCorrectGuesses.value = 0;
        runTotalGuesses.value = 0;
         if (initial.over) {
            seenLetters.value = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'];
        } else {
            seenLetters.value = initial.guessedLetters ?? [];
        }
    }

    async function nextGame() {
        state.value = STARTED;
        resultsReady.value = false;
        seenLetters.value = [];
        mistakesThisPuzzle.value = 0;
        // hearts intentionally NOT reset here: the pool carries across the daily run
        words.value = currentWords.value.map(w => w.toUpperCase());
        populateGrid(words.value);
        await selectRandomLetters();
        beginRound();
    }

    async function setWordState() {
        // Any letters already revealed at load mean this puzzle is being resumed, so its
        // per-round skill stats (time, letter guesses, low hearts) can't be measured fairly.
        const resumed = seenLetters.value.length > 0;
        words.value = currentWords.value.map(w => w.toUpperCase());
        populateGrid(words.value);
        await selectRandomLetters();
        // Reveal letters already guessed this puzzle. No heart accounting on replay:
        // the pool is authoritative from the adapter's initial state (server for the live
        // game, localStorage for archive practice).
        for (let letter of seenLetters.value) {
            selectedLetter(letter);
        }
        // Seed the mistake count from resumed state (a reload mid-puzzle): any already-seen
        // letter that isn't part of the answer was a wrong guess. The two opening letters and
        // any hints are always answer letters, so they don't count.
        mistakesThisPuzzle.value = seenLetters.value.filter(l => !correctLetters.value.includes(l)).length;
        beginRound(resumed);
        if (adapter.initialState().over) {
            revealWords();
            state.value = score.value === SERIES_LENGTH ? COMPLETED : FAILED;
            resultsReady.value = true;
        }
    }

    async function letterClicked(letter) {
        if (state.value !== STARTED || seenLetters.value.includes(letter)) {
            return;
        }
        seenLetters.value.push(letter);
        letterGuessCount++;
        const correct = selectedLetter(letter);
        runTotalGuesses.value++;
        if (correct) {
            momentum.value += MOMENTUM_LETTER;
            runCorrectGuesses.value++;
            runBestMomentum.value = Math.max(runBestMomentum.value, momentum.value);
        } else {
            momentum.value = 0;
            hearts.value = Math.max(0, hearts.value - 1);
            trackLowHearts();
            mistakesThisPuzzle.value++;
            wrongGuessNonce.value++;
        }
        checkFail();
        checkWin();
        await adapter.recordGuess([letter], hearts.value);
        await resolveOutcome(correct);
    }

    // Commit a full word: a correct guess banks a heart, a wrong one costs WRONG_WORD_PENALTY.
    // The synonym link is what lets a player commit with confidence, so this rewards deduction.
    async function commitWord(rawWord) {
        if (state.value !== STARTED) {
            return;
        }
        const guess = (rawWord ?? '').trim().toUpperCase();
        if (!guess || foundWords.value.includes(guess)) {
            return;
        }
        const correct = words.value.includes(guess);
        if (correct) {
            // A correct commit banks a heart unless the word was all but revealed already
            // (fewer than 2 letters hidden), which would just be rubber-stamping a near-done word.
            const hidden = guess.length - revealedCountForWord(guess);
            maxHiddenAtCommit = Math.max(maxHiddenAtCommit, hidden);
            const earnsHeart = hidden >= 2;
            // Momentum mirrors the heart rule: a real deduction (2+ hidden) earns the full word
            // bonus; rubber-stamping a word with only a letter or two showing just credits the
            // letters it actually revealed, so you can't farm the word bonus on near-complete words.
            momentum.value += earnsHeart ? MOMENTUM_WORD : hidden * MOMENTUM_LETTER;
            runTotalGuesses.value++;
            runCorrectGuesses.value++;
            runBestMomentum.value = Math.max(runBestMomentum.value, momentum.value);
            const newLetters = [];
            for (const letter of guess) {
                selectedLetter(letter);
                if (!seenLetters.value.includes(letter)) {
                    seenLetters.value.push(letter);
                    newLetters.push(letter);
                }
            }
            if (earnsHeart) {
                hearts.value = Math.min(MAX_HEARTS, hearts.value + 1);
            }
            checkWin();
            await adapter.recordGuess(newLetters, hearts.value);
        } else {
            momentum.value = 0;
            hearts.value = Math.max(0, hearts.value - WRONG_WORD_PENALTY);
            trackLowHearts();
            mistakesThisPuzzle.value++;
            wrongGuessNonce.value++;
            runTotalGuesses.value++;
            checkFail();
            await adapter.recordGuess([], hearts.value);
        }
        await resolveOutcome(correct, true);
    }

    // Spend a heart to reveal one still-hidden letter. Lets a stuck player turn the heart
    // pool into agency rather than just a punishment meter. A hint never ends the run, so
    // it's only offered while there's more than one heart to spend (see hintAvailable).
    async function useHint() {
        if (state.value !== STARTED || hearts.value <= 1) {
            return;
        }
        const hidden = correctLetters.value.filter(l => !seenLetters.value.includes(l));
        if (hidden.length === 0) {
            return;
        }
        const letter = hidden[Math.floor(Math.random() * hidden.length)];
        seenLetters.value.push(letter);
        selectedLetter(letter);
        // A hint reveals a letter for you, so it disqualifies a words-only "Purist" clear.
        letterGuessCount++;
        hintsUsed++;
        // Hints are only offered above 1 heart, so 2 is the lowest you can spend at: a clear from
        // here is a clutch save.
        if (hearts.value === 2) {
            clutchHint = true;
        }
        hearts.value = Math.max(0, hearts.value - 1);
        trackLowHearts();
        checkWin();
        await adapter.recordGuess([letter], hearts.value);
        await resolveOutcome(true);
    }

    function checkFail() {
        if (hearts.value <= 0) {
            state.value = FAILED;
            revealWords();
        }
    }

    function checkWin() {
        if (correctGuesses.value.length === correctLetters.value.length) {
            state.value = COMPLETED;
            seenLetters.value = []; // this is if the player refreshes the screen after completing
        }
    }

    // Shared post-guess handling for both letter guesses and word commits: sound, then
    // submit the score on completion / report game over.
    async function resolveOutcome(correct, isWord = false) {
        if (state.value === COMPLETED) {
            playFinish();
        } else if (state.value === FAILED) {
            playGameOver();
        } else if (correct) {
            isWord ? playWordCorrect() : playCorrect();
        } else {
            isWord ? playWordWrong() : playWrong();
        }

        if (state.value === FAILED) {
            const over = await adapter.gameOver();
            unlockAchievements(over?.newAchievements);
            resultsReady.value = true;
        }

        if (state.value === COMPLETED) {
            if (mistakesThisPuzzle.value === 0) {
                runFlawless.value++;
            }
            const completed = await adapter.completed({
                mistakes: mistakesThisPuzzle.value,
                // A resumed puzzle can't be timed/measured fairly, so send sentinels that
                // can't satisfy the per-round skill badges (matching the backend's defaults).
                solveSeconds: roundResumed ? -1 : Math.round((Date.now() - roundStartMs) / 1000),
                maxHiddenAtCommit: roundResumed ? 0 : maxHiddenAtCommit,
                letterGuesses: roundResumed ? -1 : letterGuessCount,
                lowestHearts: roundResumed ? 0 : lowestHearts,
                hintsUsed: roundResumed ? -1 : hintsUsed,
                clutchHint: roundResumed ? false : clutchHint,
                momentum: momentum.value,
            });
            score.value = completed.game;
            xp.value = completed.xp;
            // Re-sync to the server's authoritative momentum so the meter can't drift from what
            // actually drove the XP award (archive practice returns none, so keep the local value).
            if (completed.momentum !== undefined) {
                momentum.value = completed.momentum;
            }
            if (completed.hearts !== undefined) {
                hearts.value = completed.hearts; // a heart is awarded for completing
            }
            unlockAchievements(completed.newAchievements);
            if (score.value === SERIES_LENGTH) {
                const over = await adapter.gameOver();
                unlockAchievements(over?.newAchievements);
            }
            resultsReady.value = true;
        }
    }

    async function selectRandomLetters() {
        if (seenLetters.value.length > 0) {
            return;
        }
        if (seenLetters.value.length === 0) {
            const wordsCombined = words.value.join("");
            const letter1 = getRandomLetter('', wordsCombined);
            const letter2 = getRandomLetter(letter1, wordsCombined);
            [letter1, letter2].forEach(letter => {
                selectedLetter(letter);
                seenLetters.value.push(letter);
            });
            await adapter.recordGuess(seenLetters.value, hearts.value);
        }
    }

    const loading = computed(() => {
        return adapterLoading.value;
    })

    function isLetterACorrectGuess(letter) {
        return correctLetters.value.length > 0 && correctGuesses.value.includes(letter);
    }

    function isLetterAWrongGuess(letter) {
        return correctLetters.value.length > 0 && seenLetters.value.includes(letter) && !correctGuesses.value.includes(letter);
    }

    const correctGuesses = computed(() => {
        let correct = [];
        for (let seen of seenLetters.value) {
            if (correctLetters.value.includes(seen)) {
                correct.push(seen);
            }
        }
        return correct;
    })

    const correctLetters = computed(() => {
        const allLetters = words.value.join("").split("");
        return [...new Set(allLetters)];
    });


    function getRandomLetter(excludeLetter, words) {
        let seed = score.value + 1;
        let randomLetter = excludeLetter;
        do {
            let randomIndex = Math.floor(seedrandom(seed++)() * words.length);
            randomLetter = words[randomIndex];
        } while (randomLetter === excludeLetter);
        return randomLetter;
    }

    function getFormattedDate() {
        const currentDate = new Date();
        const year = currentDate.getUTCFullYear();
        const month = currentDate.getUTCMonth() + 1;
        const day = currentDate.getUTCDate();
        return `${year}-${month}-${day}`;
    }


    const puzzleFinished = computed(() => {
        return state.value === COMPLETED || state.value === FAILED;
    });

    const failedGame = computed(() => {
        return state.value === FAILED || score.value === SERIES_LENGTH; // not a very nice flag for completing
    })

    // Hint is offered only mid-puzzle, when spending a heart can't end the run (>1 left),
    // and when there's actually a hidden letter worth revealing.
    const hintAvailable = computed(() =>
        state.value === STARTED &&
        hearts.value > 1 &&
        correctLetters.value.some(l => !seenLetters.value.includes(l))
    );

    const momentumMult = computed(() => momentumMultiplier(momentum.value));

    // Snapshot of how the current run went, for the end-of-run report card. accuracy counts
    // only the player's own guesses (the two auto-revealed opening letters and hints aren't
    // counted); defaults to 100 before any guess so a zero-guess card doesn't read as 0%.
    const runReport = computed(() => ({
        solved: score.value,
        xpEarned: Math.max(0, xp.value - runXpStart.value),
        bestMultiplier: momentumMultiplier(runBestMomentum.value),
        flawless: runFlawless.value,
        accuracy: runTotalGuesses.value > 0
            ? Math.round((runCorrectGuesses.value / runTotalGuesses.value) * 100)
            : 100,
    }));

    return {
        nextGame,
        initialiseGame,
        hearts,
        momentum,
        momentumMult,
        maxHearts: MAX_HEARTS,
        seriesLength: SERIES_LENGTH,
        useHint,
        hintAvailable,
        commitWord,
        eligibleWords,
        puzzleFinished,
        failedGame,
        isLetterAWrongGuess,
        isLetterACorrectGuess,
        seenLetters,
        grid,
        letterClicked,
        score,
        correctGuesses,
        loading,
        foundWords,
        xp,
        resultsReady,
        wrongGuessNonce,
        runReport
    }
}